import {
  TelegramTransactionPartByAmount,
  TelegramTransactionsPaymentsSystemsResponseDto,
  TelegramTransactionsUserControllerGetTopUpVariants200,
} from '@/generated';
import { ActorRefFrom, assign, createMachine, fromPromise } from 'xstate';
import { Dict } from './types';
import { createPurchaseSubflow } from '../BuyingCoins/state';
import { services } from '../common/services';
import { isTelegramStarsPaymentError } from '@/utils/telegram-stars-payment';

type onSuccess = (prodId: string) => void;

export type FeatureCheckoutContext = {
  payments: TelegramTransactionsPaymentsSystemsResponseDto[];
  availableAmounts: TelegramTransactionsUserControllerGetTopUpVariants200;
  productId: string;
  coinsBalance: number;
  selected: string[];
  dict: Dict;
  coinsCost: number;
  fiatCost: number;
  fiatFakeCost: number;
  telegramStarsAmount: number;
  pendingPaymentSystem?: string;
  cardComDetailsData?: { parts: number; payerName: string; token?: string };
  parts?: TelegramTransactionPartByAmount[];
  partsLoading: boolean;
};

export type FeatureCheckoutInput = Pick<
  FeatureCheckoutContext,
  | 'payments'
  | 'availableAmounts'
  | 'productId'
  | 'coinsBalance'
  | 'selected'
  | 'dict'
  | 'coinsCost'
  | 'fiatCost'
  | 'fiatFakeCost'
  | 'telegramStarsAmount'
>;

export type FeatureCheckoutOutput = {
  paymentSystem?: string;
};

export type FeatureCheckoutEvents =
  | { type: 'SELECT_METHOD'; paymentSystem: string }
  | { type: 'GO_PAY_CARD_COM'; parts: number; payerName: string; token?: string }
  | { type: 'NEED_MORE_COINS' }
  | { type: 'MANUAL_TRANSFER' }
  | { type: 'PREV_PAGE' }
  | { type: 'BACK_TO_SELECTION' };

export function createFeatureCheckoutFlow(onSuccessPay: onSuccess) {
  const coinsPurchaseSubFlow = createPurchaseSubflow(services);

  return createMachine({
    types: {} as {
      context: FeatureCheckoutContext;
      events: FeatureCheckoutEvents;
      input: FeatureCheckoutInput;
      output: FeatureCheckoutOutput;
    },
    id: 'featureCheckoutFlow',
    context: ({ input }) => ({
      payments: input.payments,
      availableAmounts: input.availableAmounts,
      productId: input.productId,
      coinsBalance: input.coinsBalance,
      selected: input.selected,
      dict: input.dict,
      coinsCost: input.coinsCost,
      fiatCost: input.fiatCost,
      fiatFakeCost: input.fiatFakeCost,
      telegramStarsAmount: input.telegramStarsAmount,
      pendingPaymentSystem: undefined,
      cardComDetailsData: undefined,
      parts: undefined,
      partsLoading: false,
    }),
    initial: 'chooseMethod',
    states: {
      chooseMethod: {
        entry: ({ context }) => {
          context.partsLoading = true;
          context.parts = undefined;
        },
        invoke: {
          id: 'loadFeaturePartsForAmount',
          input: ({ context }) => ({
            featureIds: context.selected,
          }),
          src: fromPromise(async ({ input }) => {
            if (input.featureIds == null) throw new Error('No features');
            return services.getPartsOptions(input.featureIds as string[]);
          }),
          onDone: {
            actions: ({ context, event }) => {
              context.parts = event.output;
              context.partsLoading = false;
            },
          },
          onError: {
            actions: ({ context }) => {
              context.partsLoading = false;
            },
          },
        },
        on: {
          SELECT_METHOD: [
            {
              guard: ({ context, event }) =>
                event.paymentSystem === 'COINS' && context.coinsCost > context.coinsBalance,
              actions: ({ self }) => {
                self.send({ type: 'NEED_MORE_COINS' });
              },
            },
            {
              guard: ({ event }) => event.paymentSystem === 'CARD_COM',
              target: 'cardComDetails',
            },
            {
              guard: ({ event }) => event.paymentSystem === 'MANUAL',
              actions: ({ self }) => {
                self.send({ type: 'MANUAL_TRANSFER' });
              },
            },
            {
              target: 'processingPayment',
              actions: assign(({ context, event }) => {
                context.pendingPaymentSystem = event.paymentSystem;
                return context;
              }),
            },
          ],
          NEED_MORE_COINS: { target: 'purchaseCoins' },
          MANUAL_TRANSFER: 'manualTransfer',
        },
      },

      manualTransfer: {
        on: {
          PREV_PAGE: 'chooseMethod',
        },
      },
      cardComDetails: {
        on: {
          PREV_PAGE: 'chooseMethod',
          GO_PAY_CARD_COM: [
            {
              target: 'processingPayment',
              actions: assign(({ context, event }) => {
                context.cardComDetailsData = event;
                context.pendingPaymentSystem = 'CARD_COM';
                return context;
              }),
            },
          ],
        },
      },
      error: {
        on: {
          PREV_PAGE: 'chooseMethod',
          BACK_TO_SELECTION: 'chooseMethod',
        },
      },

      purchaseCoins: {
        invoke: {
          id: 'coinPurchaseFlow',
          src: coinsPurchaseSubFlow,
          input: ({ context }) => ({
            productId: context.productId,
            featureIds: context.selected,
            availableAmounts: context.availableAmounts.filter(
              (am) => am.amount + context.coinsBalance >= context.coinsCost,
            ),
            methods: context.payments.filter((val) => val.paymentSystem !== 'COINS'),
          }),
          onDone: {
            target: 'thankYou',
            actions: assign(({ context, event }) => {
              context.pendingPaymentSystem = event.output.methodId;
              return context;
            }),
          },
        },
        on: {
          PREV_PAGE: 'chooseMethod',
        },
      },

      thankYou: {
        type: 'final',
      },
      processingPayment: {
        invoke: {
          src: fromPromise(async ({ input }) => {
            const context = input.context as FeatureCheckoutContext;
            const methodId = context.pendingPaymentSystem;
            if (!methodId) {
              return;
            }

            if (methodId === 'COINS') {
              await services.handlePayment({
                productId: context.productId,
                featureIds: context.selected,
                methodId,
              });

              onSuccessPay(context.productId);

              return;
            }
            if (methodId === 'CARD_COM') {
              const cardComDetailsData = context.cardComDetailsData;
              await services.handlePayment({
                productId: context.productId,
                featureIds: context.selected,
                parts: cardComDetailsData?.parts,
                payerName: cardComDetailsData?.payerName,
                token: cardComDetailsData?.token || undefined,
                methodId,
              });
              onSuccessPay(context.productId);
              return;
            }
            await services.handlePayment({
              productId: context.productId,
              featureIds: context.selected,
              methodId,
            });
          }),
          input: ({ context }) => ({ context }),
          onDone: {
            target: 'thankYou',
          },

          onError: [
            {
              guard: ({ event }) => isTelegramStarsPaymentError(event.error, 'cancelled'),
              target: 'chooseMethod',
              actions: assign(({ context }) => {
                context.pendingPaymentSystem = undefined;
                return context;
              }),
            },
            {
              target: 'error',
              actions: assign(({ context }) => {
                context.pendingPaymentSystem = undefined;
                return context;
              }),
            },
          ],
        },
      },
    },
    output: ({ context }) => ({
      paymentSystem: context.pendingPaymentSystem,
    }),
  });
}

// Helper type for consumers
export type FeatureCheckoutFlowActor = ActorRefFrom<ReturnType<typeof createFeatureCheckoutFlow>>;
